import { useCallback, useEffect, useMemo, useState } from "react";
import api, { getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Table2, AlertTriangle } from "lucide-react";

const STYLE = {
  APPROVED: "bg-green-100 text-green-800 border-green-300",
  EXACT: "bg-green-100 text-green-800 border-green-300",
  STRONG: "bg-blue-100 text-blue-800 border-blue-300",
  SUPPORTING: "bg-blue-100 text-blue-800 border-blue-300",
  INDICATIVE: "bg-amber-100 text-amber-800 border-amber-300",
  NONE: "bg-slate-100 text-slate-700 border-slate-300",
  PENDING: "bg-slate-100 text-slate-700 border-slate-300",
  SYSTEM_PROPOSED: "bg-amber-100 text-amber-800 border-amber-300",
  REQUIRES_AUDITOR_JUDGMENT: "bg-red-100 text-red-800 border-red-300",
};

const METHOD_LABELS = {
  EXACT_PRIOR_APPROVED_MAPPING: "Pemetaan disetujui sebelumnya",
  FIRM_MAPPING_LIBRARY: "Pustaka pemetaan KAP",
  STRUCTURAL_ACCOUNT_CODE_RULE: "Aturan struktur kode akun",
  APPROVED_PARENT_CHILD_MAPPING: "Mengikuti akun induk",
  TRANSACTION_BEHAVIOR: "Perilaku transaksi",
  DOMAIN_MODULE_EVIDENCE: "Bukti modul siklus",
  NAME_BASED_HEURISTIC: "Dugaan dari nama akun",
  REQUIRES_AUDITOR_JUDGMENT: "Perlu keputusan auditor",
};

const FILTER_LABELS = {
  all: "Semua", pending: "Menunggu", heuristic: "Dugaan nama",
  judgment: "Perlu keputusan", conflict: "Bertentangan", approved: "Disetujui",
  high_balance: "Saldo besar", high_movement: "Pergerakan besar",
  materiality_relevant: "Relevan materialitas", tax_related: "Terkait pajak",
  manual_journal_heavy: "Banyak jurnal manual",
};

const text = (v) => v ?? "—";

function Badge({ kind, label }) {
  return (
    <span className={`ml-1 font-normal inline-block px-2 py-0.5 border rounded-sm ${STYLE[kind] || ""}`}>
      {label || text(kind)}
    </span>
  );
}

function Figure({ label, value }) {
  return (
    <div className="border border-border rounded-sm p-2">
      <div className="text-muted-foreground">{label}</div>
      <div className="font-medium tabular-nums">{value ?? "—"}</div>
    </div>
  );
}

// COA & FS Mapping — antrean reviu klasifikasi bagan akun. Layar ini tidak menyetujui apa pun;
// persetujuan dilakukan melalui modul bagan akun yang sudah mengatur peran dan log audit.
export default function CoaReview({ id }) {
  const [view, setView] = useState(null);
  const [filter, setFilter] = useState("all");
  const [draft, setDraft] = useState({});
  const [open, setOpen] = useState({});
  const [saving, setSaving] = useState(null);

  const load = useCallback((active) => {
    api.get(`/engagements/${id}/coa-review`, { params: { view: active || "all" } })
      .then(({ data }) => setView(data))
      .catch((e) => toast.error(getErrorMessage(e)));
  }, [id]);
  useEffect(() => { load(filter); }, [load, filter]);

  const edit = (code, field, value) =>
    setDraft({ ...draft, [code]: { ...(draft[code] || {}), [field]: value } });

  const decide = (row, action) => {
    const pending = draft[row.account_code] || {};
    const body = {
      account_type: pending.account_type ?? row.suggested_account_type,
      fs_line: pending.fs_line ?? row.suggested_fs_line,
      audit_area: pending.audit_area ?? row.suggested_audit_area,
      normal_balance: pending.normal_balance ?? row.suggested_normal_balance,
      classification: pending.classification ?? row.suggested_classification,
      reason: pending.reason || "",
    };
    if (action === "EDIT_MAPPING" && !body.reason.trim()) {
      toast.error("Alasan penggantian pemetaan harus diisi."); return;
    }
    if (!body.account_type || !body.fs_line) {
      toast.error("Tipe akun dan baris laporan keuangan harus ditentukan."); return;
    }
    setSaving(row.account_code);
    api.put(`/coa/${row.account_id}`, body)
      .then(() => { toast.success(`${row.account_code} disimpan.`); load(filter); })
      .catch((e) => toast.error(getErrorMessage(e)))
      .finally(() => setSaving(null));
  };

  const groups = useMemo(() => view?.groups || [], [view]);

  if (!view) {
    return <div className="text-xs text-muted-foreground p-4">Memuat pemetaan bagan akun…</div>;
  }
  const coverage = view.coverage || {};

  return (
    <div className="space-y-3 text-xs" data-testid="coa-review">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="coa-review-summary">
        <Figure label="Akun neraca saldo" value={view.total_accounts} />
        <Figure label="Sudah diklasifikasi" value={coverage.classified_accounts} />
        <Figure label="Belum diklasifikasi" value={coverage.unclassified_accounts} />
        <Figure label="Cakupan menurut nilai"
          value={coverage.coverage_by_value ? `${String(coverage.coverage_by_value).slice(0, 5)}%` : "0%"} />
        <Figure label="Dugaan dari nama" value={view.by_method?.NAME_BASED_HEURISTIC || 0} />
        <Figure label="Perlu keputusan" value={view.requires_judgment} />
        <Figure label="Bertentangan" value={view.conflicts} />
        <Figure label="Dapat disetujui massal" value={view.bulk_approvable} />
      </div>

      {view.bulk_approvable === 0 && (
        <div className="bg-white border border-amber-300 rounded-sm p-2 text-amber-900"
          data-testid="coa-review-no-bulk">
          <AlertTriangle size={13} className="inline mr-1" />
          Tidak ada usulan yang memenuhi syarat persetujuan massal. Usulan berbasis nama akun tidak
          pernah disetujui secara massal, dan hanya pemetaan yang sudah pernah disetujui atau
          berasal dari pustaka KAP yang boleh — itu pun bila tidak ada sinyal yang bertentangan.
        </div>
      )}

      <div className="flex flex-wrap gap-1" data-testid="coa-review-filters">
        {(view.filters || []).map((name) => (
          <button key={name} onClick={() => setFilter(name)}
            data-testid={`coa-filter-${name}`}
            className={`px-2 py-1 rounded-sm border ${filter === name
              ? "bg-slate-900 text-white border-slate-900" : "border-border"}`}>
            {FILTER_LABELS[name] || name}
          </button>
        ))}
      </div>

      {groups.length === 0 && (
        <div className="text-muted-foreground" data-testid="coa-review-empty">
          Tidak ada akun pada penyaring ini.
        </div>
      )}

      {groups.map((group) => (
        <div key={group.group} className="bg-white border border-border rounded-sm"
          data-testid={`coa-group-${group.group}`}>
          <button className="w-full text-left p-2 font-head font-semibold flex flex-wrap gap-2"
            data-testid={`coa-group-toggle-${group.group}`}
            onClick={() => setOpen({ ...open, [group.group]: !open[group.group] })}>
            <span>{group.group}</span>
            <span className="text-muted-foreground font-normal">
              {group.count} akun · {group.pending} menunggu
              {group.conflicts ? ` · ${group.conflicts} bertentangan` : ""}
            </span>
          </button>
          {open[group.group] && (
            // The queue has fifteen columns: it scrolls inside its own box so the page never shifts sideways (B-445).
            <div className="overflow-x-auto" data-testid={`coa-group-scroll-${group.group}`}>
            <table className="w-full audit-table text-xs">
              <thead><tr>
                <th>Kode</th><th>Nama akun</th><th className="text-right">Saldo</th>
                <th className="text-right">Saldo lalu</th><th>Tipe usulan</th><th>Baris LK</th>
                <th>Area</th><th>Metode</th><th>Dasar</th><th>Keyakinan</th>
                <th>Perilaku</th><th>Konflik</th><th>Status</th><th>Disetujui oleh</th>
                <th>Tindakan</th>
              </tr></thead>
              <tbody>{group.rows.map((row) => {
                const pending = draft[row.account_code] || {};
                return (
                  <tr key={row.account_code} data-testid={`coa-row-${row.account_code}`}>
                    <td className="font-mono">{row.account_code}</td>
                    <td>{row.account_name}</td>
                    <td className="text-right tabular-nums">{text(row.current_balance_exact ?? row.current_balance)}</td>
                    <td className="text-right tabular-nums">{text(row.prior_balance_exact ?? row.prior_balance)}</td>
                    <td>
                      <select className="border border-border rounded-sm px-1 py-0.5"
                        data-testid={`coa-type-${row.account_code}`}
                        value={pending.account_type ?? (row.suggested_account_type || "")}
                        onChange={(e) => edit(row.account_code, "account_type", e.target.value)}>
                        <option value="">—</option>
                        {(view.vocabulary?.account_types || []).map((t) => (
                          <option key={t} value={t}>{t}</option>))}
                      </select>
                    </td>
                    <td>
                      <select className="border border-border rounded-sm px-1 py-0.5"
                        data-testid={`coa-line-${row.account_code}`}
                        value={pending.fs_line ?? (row.suggested_fs_line || "")}
                        onChange={(e) => edit(row.account_code, "fs_line", e.target.value)}>
                        <option value="">—</option>
                        {(view.vocabulary?.fs_lines || []).map((l) => (
                          <option key={l} value={l}>{l}</option>))}
                      </select>
                    </td>
                    <td>{text(row.suggested_audit_area)}</td>
                    <td>{METHOD_LABELS[row.proposal_method] || row.proposal_method}</td>
                    <td className="min-w-[14rem]">{row.proposal_basis}</td>
                    <td><Badge kind={row.proposal_confidence} /></td>
                    <td>{row.behaviour?.note || "—"}</td>
                    <td>{(row.conflicts || []).length
                      ? <span className="text-red-800">
                          {row.conflicts.map((c) => c.note).join(" ")}
                        </span>
                      : "—"}</td>
                    <td><Badge kind={row.decision} /></td>
                    <td>{text(row.approved_by)}</td>
                    <td>
                      <div className="flex flex-col gap-1">
                        <input className="border border-border rounded-sm px-1 py-0.5 w-32"
                          placeholder="Alasan (wajib bila diubah)"
                          data-testid={`coa-reason-${row.account_code}`}
                          value={pending.reason || ""}
                          onChange={(e) => edit(row.account_code, "reason", e.target.value)} />
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline"
                            disabled={saving === row.account_code}
                            data-testid={`coa-accept-${row.account_code}`}
                            onClick={() => decide(row, "ACCEPT_PROPOSAL")}>Terima</Button>
                          <Button size="sm" variant="outline"
                            disabled={saving === row.account_code}
                            data-testid={`coa-edit-${row.account_code}`}
                            onClick={() => decide(row, "EDIT_MAPPING")}>Ubah</Button>
                        </div>
                      </div>
                    </td>
                  </tr>);
              })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      ))}

      <div className="text-muted-foreground" data-testid="coa-review-basis">
        {view.basis} Persetujuan dikirim ke {view.approval_route}.
      </div>
    </div>
  );
}
